"use client"

import { useState } from "react"

import MuxPlayer from "@/components/marketing/lazy-mux-player"
import { ACCENT, COLORS } from "@/lib/design-tokens"
import { MUX_PLAYBACK_RATES } from "@/lib/mux-player-config"
import { TESTIMONIAL_VIDEO_ENTRIES } from "@/lib/testimonial-videos"

type TestimonialVideo = {
  id: string
  title: string
  playbackId: string
}

function buildTestimonialVideos(): TestimonialVideo[] {
  // Only real Mux-hosted clips, no placeholders, no titles, no text on a video
  // (owner: "don't have placeholders, just when we have more videos add them,
  // and don't title them or have any text at all, just let the video be
  // there"). The grid renders nothing when there are none.
  return TESTIMONIAL_VIDEO_ENTRIES.flatMap((entry) =>
    entry.playbackId ? [{ id: entry.id, title: entry.title, playbackId: entry.playbackId }] : [],
  )
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 4.5v15l13-7.5z" fill="currentColor" />
    </svg>
  )
}

/** One portrait 9:16 card: the video's poster with a play button, swapped for
 *  the Mux player on click. Portrait because the clips are phone selfie video. */
function TestimonialVideoCard({
  video,
  index,
  playing,
  onPlay,
}: {
  video: TestimonialVideo
  index: number
  playing: boolean
  onPlay: () => void
}) {
  return (
    <article className={`tpv-card${playing ? " is-playing" : ""}`}>
      {playing ? (
        <div className="tpv-player">
          <MuxPlayer
            streamType="on-demand"
            playbackId={video.playbackId}
            autoPlay
            playbackRates={MUX_PLAYBACK_RATES}
            thumbnailTime={1}
            metadata={{
              video_id: `testimonial-${video.id}`,
              video_title: `Cold Approach testimonial - ${video.title}`,
            }}
            primaryColor={COLORS.paper}
            secondaryColor={COLORS.ink}
            accentColor={ACCENT}
            style={{ height: "100%", width: "100%" }}
          />
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            className="tpv-poster"
            src={`https://image.mux.com/${video.playbackId}/thumbnail.jpg?time=1&width=1080`}
            alt=""
            loading="lazy"
          />
          <button type="button" className="tpv-play" aria-label={`Play client video ${index + 1}`} onClick={onPlay}>
            <span>
              <PlayGlyph />
            </span>
          </button>
        </>
      )}
    </article>
  )
}

export function VideoTestimonialsSection() {
  const videos = buildTestimonialVideos()
  const [playingId, setPlayingId] = useState<string | null>(null)
  if (videos.length === 0) return null

  return (
    <div className="tpv-grid">
      {videos.map((video, i) => (
        <TestimonialVideoCard
          key={video.id}
          video={video}
          index={i}
          playing={playingId === video.id}
          onPlay={() => setPlayingId(video.id)}
        />
      ))}
    </div>
  )
}
